"use client";

import React from "react";
import { Loader, Send, Sparkles } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function InitiateConnection() {
  const {
    ref: textRef,
    isVisible: textVisible
  } = useRevealOnScroll();
  const {
    ref: formRef,
    isVisible: formVisible
  } = useRevealOnScroll();
  return <section data-section-id="17513" id="initialize-connection" className="dark bg-background text-foreground relative min-h-screen w-full overflow-hidden py-24 px-4 md:px-8 lg:px-16 flex flex-col justify-center">
      {}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_top_left,_var(--primary)_0%,_transparent_25%)] opacity-20" />
        <div className="absolute bottom-0 right-0 w-full h-full bg-[radial-gradient(circle_at_bottom_right,_var(--primary)_0%,_transparent_20%)] opacity-10" />
        <div className="absolute inset-0 bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1626701/img/grid_pattern_dark.png')] opacity-[0.05]" />
      </div>

      <div className="container mx-auto relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          {}
          <div ref={textRef} className={`transition-all duration-1000 ease-out transform ${textVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-10"}`}>
            <h2 className="text-4xl md:text-7xl lg:text-8xl font-default font-black uppercase tracking-tighter leading-[0.9] text-transparent bg-clip-text bg-gradient-to-br from-white via-primary to-primary drop-shadow-[0_0_20px_rgba(0,255,0,0.3)]">
              Initialize
              <br />
              <span className="text-primary drop-shadow-[0_0_30px_rgba(0,255,0,0.6)]">
                Connection
              </span>
            </h2>
            <p className="mt-8 text-base md:text-xl font-mono text-muted-foreground max-w-xs md:max-w-md border-l-4 border-primary pl-6">
              Establish a secure link to the V-IRAL gatekeeper. Deploy educational challenges and regain control of the digital landscape.
            </p>
            
            <div className="mt-12 flex items-center gap-4 text-sm font-mono text-primary uppercase tracking-widest">
              <Sparkles className="w-4 h-4 text-primary animate-pulse" />
              <span className="animate-pulse">System Online: Handshake Ready</span>
            </div>
          </div>

          {}
          <div ref={formRef} className={`transition-all duration-1000 ease-out delay-300 transform ${formVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
            <div className="relative p-1">
              {}
              <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-primary shadow-[0_0_10px_rgba(0,255,0,0.5)]" />
              <div className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-primary shadow-[0_0_10px_rgba(0,255,0,0.5)]" />
              <div className="absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 border-primary shadow-[0_0_10px_rgba(0,255,0,0.5)]" />
              <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-primary shadow-[0_0_10px_rgba(0,255,0,0.5)]" />

              <FormProvider formKey="initialize_connection_form_home" formId="initialize_connection_form_home" sectionName="initialize-connection" className="space-y-8 p-6 md:p-10 backdrop-blur-md bg-card/40 border border-primary/20" defaultValues={{
              full_name: "",
              email: "",
              phone: "",
              company: "",
              message: ""
            }} validationRules={{
              full_name: {
                required: "Identity verification failed: Name required"
              },
              email: {
                required: "Node address required",
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: "Invalid node address format"
                }
              },
              phone: {
                required: false
              },
              company: {
                required: false
              },
              message: {
                required: false
              }
            }} submitText="Transmit Signal" successMessage="CONNECTION ESTABLISHED">
                <ContactFormContent />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>

      {}
      <style jsx global>{`
        @keyframes scanline {
          0% {
            transform: translateX(-100%);
            opacity: 0;
          }
          50% {
            opacity: 0.8;
          }
          100% {
            transform: translateX(100%);
            opacity: 0;
          }
        }
        .animate-scanline {
          animation: scanline 3s linear infinite;
        }
      `}</style>
    </section>;
}
function ContactFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="w-20 h-20 rounded-none bg-primary/20 flex items-center justify-center border-2 border-primary shadow-[0_0_40px_rgba(0,255,0,0.5)]">
          <Send className="w-10 h-10 text-primary" />
        </div>
        <h3 className="text-3xl font-default font-bold text-primary tracking-widest uppercase">
          Transmission Complete
        </h3>
        <p className="text-primary font-mono text-lg animate-pulse">{successMessage}</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-6">
        <FormField control={control} name="full_name" render={({
        field
      }) => <FormItem className="group relative">
              <FormLabel className="text-muted-foreground group-focus-within:text-primary transition-colors font-mono uppercase text-xs tracking-widest">
                
              </FormLabel>
              <FormControl>
                <div className="relative">
                  <Input placeholder="ENTER NAME..." {...field} className="bg-transparent border-0 border-b-2 border-muted/50 rounded-none px-0 py-6 font-mono focus-visible:ring-0 focus-visible:border-primary focus-visible:shadow-[0_10px_20px_-10px_rgba(0,255,0,0.3)] transition-all duration-300 placeholder:text-muted-foreground/30 text-lg" />
                  <div className="absolute bottom-0 left-0 w-full h-[2px] bg-primary scale-x-0 group-focus-within:scale-x-100 transition-transform duration-500 origin-left" />
                  <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-0 group-focus-within:opacity-100 transition-opacity duration-300">
                    <div className="absolute top-0 bottom-0 w-1/4 bg-gradient-to-r from-transparent via-primary/20 to-transparent skew-x-12 animate-scanline" />
                  </div>
                </div>
              </FormControl>
              <FormMessage className="font-mono text-[10px] text-destructive uppercase" />
            </FormItem>} />

        <FormField control={control} name="email" render={({
        field
      }) => <FormItem className="group relative">
              <FormLabel className="text-muted-foreground group-focus-within:text-primary transition-colors font-mono uppercase text-xs tracking-widest">
                
              </FormLabel>
              <FormControl>
                <div className="relative">
                  <Input type="email" placeholder="NODE@V-IRAL.AI" {...field} className="bg-transparent border-0 border-b-2 border-muted/50 rounded-none px-0 py-6 font-mono focus-visible:ring-0 focus-visible:border-primary focus-visible:shadow-[0_10px_20px_-10px_rgba(0,255,0,0.3)] transition-all duration-300 placeholder:text-muted-foreground/30 text-lg" />
                  <div className="absolute bottom-0 left-0 w-full h-[2px] bg-primary scale-x-0 group-focus-within:scale-x-100 transition-transform duration-500 origin-left" />
                  <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-0 group-focus-within:opacity-100 transition-opacity duration-300">
                    <div className="absolute top-0 bottom-0 w-1/4 bg-gradient-to-r from-transparent via-primary/20 to-transparent skew-x-12 animate-scanline" />
                  </div>
                </div>
              </FormControl>
              <FormMessage className="font-mono text-[10px] text-destructive uppercase" />
            </FormItem>} />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <FormField control={control} name="phone" render={({
          field
        }) => <FormItem className="group relative">
                <FormLabel className="text-muted-foreground group-focus-within:text-primary transition-colors font-mono uppercase text-xs tracking-widest">
                  
                </FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input type="tel" placeholder="+00 000 000 000" {...field} className="bg-transparent border-0 border-b-2 border-muted/50 rounded-none px-0 py-6 font-mono focus-visible:ring-0 focus-visible:border-primary focus-visible:shadow-[0_10px_20px_-10px_rgba(0,255,0,0.3)] transition-all duration-300 placeholder:text-muted-foreground/30 text-lg" />
                    <div className="absolute bottom-0 left-0 w-full h-[2px] bg-primary scale-x-0 group-focus-within:scale-x-100 transition-transform duration-500 origin-left" />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="company" render={({
          field
        }) => <FormItem className="group relative">
                <FormLabel className="text-muted-foreground group-focus-within:text-primary transition-colors font-mono uppercase text-xs tracking-widest">
                  
                </FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input placeholder="CORP_ID" {...field} className="bg-transparent border-0 border-b-2 border-muted/50 rounded-none px-0 py-6 font-mono focus-visible:ring-0 focus-visible:border-primary focus-visible:shadow-[0_10px_20px_-10px_rgba(0,255,0,0.3)] transition-all duration-300 placeholder:text-muted-foreground/30 text-lg" />
                    <div className="absolute bottom-0 left-0 w-full h-[2px] bg-primary scale-x-0 group-focus-within:scale-x-100 transition-transform duration-500 origin-left" />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <FormField control={control} name="message" render={({
        field
      }) => <FormItem className="group relative">
              <FormLabel className="text-muted-foreground group-focus-within:text-primary transition-colors font-mono uppercase text-xs tracking-widest">
                
              </FormLabel>
              <FormControl>
                <div className="relative">
                  <Textarea placeholder="INPUT TRANSMISSION DATA..." {...field} className="bg-transparent border-0 border-b-2 border-muted/50 rounded-none px-0 py-6 min-h-[120px] resize-none font-mono focus-visible:ring-0 focus-visible:border-primary focus-visible:shadow-[0_10px_20px_-10px_rgba(0,255,0,0.3)] transition-all duration-300 placeholder:text-muted-foreground/30 text-lg" />
                  <div className="absolute bottom-0 left-0 w-full h-[2px] bg-primary scale-x-0 group-focus-within:scale-x-100 transition-transform duration-500 origin-left" />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 bg-destructive/20 border border-destructive text-destructive font-mono text-xs rounded-none uppercase animate-pulse">
          ERROR: {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-10 md:h-16 mt-8 bg-primary hover:bg-primary/90 text-primary-foreground rounded-none shadow-[0_0_20px_rgba(0,255,0,0.4)] hover:shadow-[0_0_40px_rgba(0,255,0,0.7)] transition-all duration-300 uppercase tracking-widest text-sm md:text-xl font-default font-black group relative overflow-hidden flex items-center justify-center">
        {isSubmitting ? <span className="flex items-center justify-center gap-3">
            <Loader className="size-5 md:size-6 animate-spin" />
            SYNCHRONIZING...
          </span> : <span className="flex items-center justify-center gap-3">
            Transmit Signal
            <Send className="size-5 md:size-6 group-hover:translate-x-2 group-hover:-translate-y-1 transition-transform" />
          </span>}
        <div className="absolute inset-0 w-full h-full pointer-events-none overflow-hidden opacity-30">
          <div className="absolute top-0 bottom-0 w-1/2 bg-white/20 skew-x-12 animate-scanline" />
        </div>
      </Button>
    </Form>;
}